import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type LegalKey = 'privacy' | 'terms' | null;
const LEGAL_CONTENT: Record<Exclude<LegalKey, null>, {
  title: string;
  body: React.ReactNode;
}> = {
  privacy: {
    title: 'Política de Privacidad',
    body: <div className="space-y-4 text-slate-light leading-relaxed" data-id="element-36">
        <p data-id="element-37">
          En Pañal Adiós respetamos tu privacidad y nos comprometemos a proteger
          la información personal que compartes con nosotros al adquirir el
          Protocolo "Pañal Adiós" (3 Días).
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-38">
          1. Información que recopilamos
        </h3>
        <p data-id="element-39">
          Recopilamos únicamente la información necesaria para procesar tu
          compra y entregarte el acceso al protocolo: nombre, correo electrónico
          y datos de pago procesados de forma segura por nuestra pasarela.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-40">
          2. Uso de la información
        </h3>
        <p data-id="element-41">
          Tus datos se utilizan exclusivamente para entregarte el producto
          digital, enviarte actualizaciones relacionadas con tu compra y, si lo
          autorizas, comunicarte recursos adicionales para acompañar el proceso.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-42">
          3. Protección de datos
        </h3>
        <p data-id="element-43">
          Implementamos medidas técnicas y organizativas para proteger tu
          información. Nunca vendemos ni compartimos tus datos con terceros con
          fines comerciales.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-44">4. Tus derechos</h3>
        <p data-id="element-45">
          Tienes derecho a acceder, rectificar o eliminar tus datos personales
          en cualquier momento solicitándolo a través de nuestros canales
          oficiales.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-46">5. Cookies</h3>
        <p data-id="element-47">
          Utilizamos cookies para mejorar tu experiencia de navegación y
          analizar el rendimiento del sitio. Puedes configurarlas desde tu
          navegador.
        </p>
        <p className="text-xs opacity-70 pt-4" data-id="element-48">
          Última actualización: {new Date().getFullYear()}
        </p>
      </div>
  },
  terms: {
    title: 'Términos y Condiciones',
    body: <div className="space-y-4 text-slate-light leading-relaxed" data-id="element-49">
        <p data-id="element-50">
          Al adquirir el Protocolo "Pañal Adiós" (3 Días) aceptas los siguientes
          términos y condiciones de uso.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-51">
          1. Naturaleza del producto
        </h3>
        <p data-id="element-52">
          El Protocolo "Pañal Adiós" es un producto digital educativo dirigido a
          padres y cuidadores. No sustituye la asesoría médica, psicológica ni
          pediátrica profesional.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-53">
          2. Acceso y entrega
        </h3>
        <p data-id="element-54">
          Una vez confirmado el pago, recibirás acceso inmediato al material
          digital a través del correo electrónico proporcionado durante la
          compra.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-55">
          3. Garantía de satisfacción
        </h3>
        <p data-id="element-56">
          Ofrecemos una garantía de satisfacción. Si el producto no cumple tus
          expectativas, puedes solicitar reembolso siguiendo nuestras políticas
          internas dentro del plazo establecido.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-57">4. Uso permitido</h3>
        <p data-id="element-58">
          El contenido es de uso personal e intransferible. Está prohibida su
          reproducción, redistribución o reventa total o parcial sin
          autorización por escrito.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-59">5. Resultados</h3>
        <p data-id="element-60">
          Los resultados pueden variar según cada niño y cada familia. No
          garantizamos resultados específicos, ya que dependen de múltiples
          factores individuales.
        </p>
        <h3 className="font-bold text-slate text-lg pt-2" data-id="element-61">6. Modificaciones</h3>
        <p data-id="element-62">
          Nos reservamos el derecho de actualizar estos términos en cualquier
          momento. Los cambios entrarán en vigor al ser publicados en el sitio.
        </p>
        <p className="text-xs opacity-70 pt-4" data-id="element-63">
          Última actualización: {new Date().getFullYear()}
        </p>
      </div>
  }
};
export function Footer() {
  const currentYear = new Date().getFullYear();
  const [openModal, setOpenModal] = useState<LegalKey>(null);
  useEffect(() => {
    if (openModal) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [openModal]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return <>
      <footer className="bg-slate text-slate-light py-12 text-center" data-id="element-64">
        <div className="max-w-4xl mx-auto px-4" data-id="element-65">
          <p className="mb-4" data-id="element-66">
            © {currentYear} Pañal Adiós. Todos los derechos reservados.
          </p>
          <div className="flex justify-center gap-6 text-sm" data-id="element-67">
            <button type="button" onClick={() => setOpenModal('privacy')} className="hover:text-white transition-colors underline-offset-4 hover:underline" data-id="element-68">
              Política de Privacidad
            </button>
            <button type="button" onClick={() => setOpenModal('terms')} className="hover:text-white transition-colors underline-offset-4 hover:underline" data-id="element-69">
              Términos y Condiciones
            </button>
          </div>
          <p className="mt-8 text-xs opacity-50 max-w-2xl mx-auto" data-id="element-70">
            Este sitio no es parte del sitio web de Facebook o Facebook Inc.
            Además, este sitio NO está respaldado por Facebook de ninguna
            manera. FACEBOOK es una marca registrada de FACEBOOK, Inc.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-71">
        {openModal && <motion.div key="legal-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} data-id="element-72">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpenModal(null)} aria-hidden="true" data-id="element-73" />
            <motion.div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" initial={{
          opacity: 0,
          y: 20,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.96
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} data-id="element-74">
              <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-slate-200 bg-cream" data-id="element-75">
                <h2 id="legal-modal-title" className="text-xl md:text-2xl font-bold text-slate" data-id="element-76">
                  {LEGAL_CONTENT[openModal].title}
                </h2>
                <button type="button" onClick={() => setOpenModal(null)} aria-label="Cerrar" className="p-2 rounded-full hover:bg-slate-200/60 transition-colors text-slate" data-id="element-77">
                  <X size={20} data-id="element-78" />
                </button>
              </div>
              <div className="px-6 md:px-8 py-6 overflow-y-auto" data-id="element-79">
                {LEGAL_CONTENT[openModal].body}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}